import { useRef } from "react";
import type { KeyboardEvent } from "react";
import { cx } from "../../components";
import styles from "./GrilleMois.module.css";
import {
  ENTETES_SEMAINE,
  NOMS_SEMAINE,
  cycle,
  decalageLundi,
  estFutur,
  estWeekend,
  joursDuMois,
  libelleDemiJournees,
  libelleJour,
} from "./mois";
import type { DemiJournees, Grille, Mois } from "./mois";

export type GrilleMoisProps = {
  mois: Mois;
  valeurs: Grille;
  /** Aujourd'hui, en ISO local : au-delà, les jours sont grisés et inertes. */
  aujourdhui: string;
  /** Absent : lecture seule. */
  onChange?: (jour: string, valeur: DemiJournees) => void;
  /** Vrai le temps d'un enregistrement : la grille reste lisible, pas cliquable. */
  disabled?: boolean;
};

/**
 * La grille d'un mois : un bouton par jour, qui cycle rien → ½ → 1.
 *
 * Chaque jour est un vrai bouton, autoportant : son état se lit dans
 * `aria-pressed` (faux, `mixed` pour la demi-journée, vrai pour la journée)
 * et son étiquette dit le jour et la valeur — un lecteur d'écran annonce
 * « mercredi 3 septembre, demi-journée », sans avoir à lire la grille.
 *
 * Les flèches déplacent le focus d'un jour (gauche/droite) ou d'une semaine
 * (haut/bas) : un mois se remplit au clavier sans tabuler trente fois.
 *
 * Les jours à venir sont désactivés plutôt que masqués : on voit que le mois
 * continue, on comprend pourquoi on ne peut pas y toucher.
 */
export function GrilleMois({ mois, valeurs, aujourdhui, onChange, disabled = false }: GrilleMoisProps) {
  const jours = joursDuMois(mois);
  const vides = decalageLundi(mois);
  const lectureSeule = onChange === undefined;
  const refGrille = useRef<HTMLDivElement | null>(null);

  function auClavier(evenement: KeyboardEvent<HTMLDivElement>) {
    const pas: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 7, ArrowUp: -7 };
    const delta = pas[evenement.key];
    if (delta === undefined || !(evenement.target instanceof HTMLElement)) {
      return;
    }
    const courant = evenement.target.dataset.jour;
    if (courant === undefined) {
      return;
    }
    const index = jours.indexOf(courant);
    const cible = jours[index + delta];
    if (cible === undefined) {
      return;
    }
    evenement.preventDefault();
    refGrille.current?.querySelector<HTMLButtonElement>(`[data-jour="${cible}"]`)?.focus();
  }

  return (
    <div className={styles.grille} ref={refGrille} onKeyDown={lectureSeule ? undefined : auClavier}>
      {ENTETES_SEMAINE.map((lettre, i) => (
        <div key={NOMS_SEMAINE[i]} className={styles.entete} aria-hidden="true" title={NOMS_SEMAINE[i]}>
          {lettre}
        </div>
      ))}
      {Array.from({ length: vides }, (_, i) => (
        <div key={`vide-${i}`} className={styles.vide} aria-hidden="true" />
      ))}
      {jours.map((jour) => {
        const valeur = valeurs[jour] ?? 0;
        const futur = estFutur(jour, aujourdhui);
        const classes = cx(
          styles.jour,
          estWeekend(jour) && styles.weekend,
          jour === aujourdhui && styles.aujourdhui,
          valeur === 1 && styles.demi,
          valeur === 2 && styles.plein,
          futur && styles.futur,
        );
        const numero = Number.parseInt(jour.slice(8, 10), 10);
        const etiquette = `${libelleJour(jour)} : ${libelleDemiJournees(valeur)}`;

        if (lectureSeule) {
          return (
            <span key={jour} className={classes} role="img" aria-label={etiquette}>
              <span className={styles.numero}>{numero}</span>
              <Marque valeur={valeur} />
            </span>
          );
        }
        return (
          <button
            key={jour}
            type="button"
            data-jour={jour}
            className={classes}
            aria-pressed={valeur === 0 ? false : valeur === 1 ? "mixed" : true}
            aria-label={futur ? `${libelleJour(jour)} : à venir` : etiquette}
            disabled={disabled || futur}
            onClick={() => onChange(jour, cycle(valeur))}
          >
            <span className={styles.numero}>{numero}</span>
            <Marque valeur={valeur} />
          </button>
        );
      })}
    </div>
  );
}

/** La marque visuelle d'une valeur : rien, un demi-disque, un disque plein. */
function Marque({ valeur }: { valeur: DemiJournees }) {
  if (valeur === 0) {
    return <span className={styles.marqueVide} aria-hidden="true" />;
  }
  return <span className={cx(styles.marque, valeur === 1 && styles.marqueDemi)} aria-hidden="true" />;
}
